<template>
  <div>
    <hr class="mt-1 mb-2">
    <div
      class="d-flex align-items-center"
      style="height: 28px"
    >
      <div
        class="mr-2 checkbox align-self-center"
        @click="checkAll('none')"
      >
        <Checkbox
          :value="groupChecks['none']"
          :pending="groupCheckPending['none']"
          :emit="false"
          style="position: relative; margin-left: -5px"
          class="px-0"
        />
      </div>

      <div
        v-if="checkedOrders.length > 0"
        class="flex-grow-1 d-flex align-items-center"
      >
        <Tippy
          ref="archive-popup"
          trigger="click"
          theme="light"
          arrow
          interactive
        >
          <template #trigger>
            <button
              v-tippy
              :content="$t('shop.cancel')"
              class="btn btn-sm btn-outline-primary icon-btn mx-1"
            >
              <i class="fas fa-ban" />
            </button>
          </template>

          <button
            class="btn btn-primary btn-sm btn-block"
            :disabled="pendingCancel"
            @click="requestRemoveSubscriptions"
          >
            <i class="fas fa-ban pr-2" />
            {{ $t('shop.cancel') }}
          </button>
        </Tippy>

        <button
          v-if="!reserveQueue"
          v-tippy
          :content="$t('shop.moveToReserveQueue')"
          class="btn btn-sm btn-outline-primary icon-btn mx-1"
          :disabled="pendingMove"
          @click="moveToReserveQueueChecked"
        >
          <i class="fas fa-clock" />
        </button>

        <button
          v-else
          v-tippy
          :content="$t('shop.promoteFromReserveQueue')"
          class="btn btn-sm btn-outline-primary icon-btn mx-1"
          :disabled="pendingPromote"
          @click="promoteCheckedFromReserveQueue"
        >
          <i class="fas fa-arrow-up" />
        </button>

        <button
          v-tippy
          :content="$t('shop.moveToListing')"
          class="btn btn-sm btn-outline-primary icon-btn mx-1"
          @click="moveToListingChecked"
        >
          <i class="fas fa-right-left" />
        </button>

        <button
          v-tippy
          class="btn btn-outline-primary icon-btn mx-1"
          :content="$t('messages.newMessage')"
          @click="showAddMessage"
        >
          <i class="fas fa-comments" />
          <i class="fas fa-plus xs-add" />
        </button>

        <div class="pl-2 small text-primary">
          {{ $t('shop.subscriptions') }}: {{ checkedOrders.length }}
        </div>
      </div>
      <div
        v-else
        class="flex-grow-1"
      />
      <template v-if="!editedId">
        <div class="d-flex align-items-center">
          <div class="summary-label mr-3">
            {{ $t('shop.summary') }}:
          </div>
          <div
            class="money-font pr-2"
            style="border-right: 1px solid #ddd"
          >
            <i class="far fa-box" />
            x{{ addSpaces(totalCount) }}
          </div>
          <div
            class="money-font text-right px-2"
          >
            <i class="far fa-money-bill-wave" />
            {{ formatCurrency(totalPrice.price, totalPrice.currency) }}
          </div>
        </div>
      </template>
    </div>
    <hr class="mb-0 mt-2">

    <BModal
      v-model="showBlockedItems"
      hide-footer
      hide-header
    >
      <div class="text-center mb-2">
        <div class="font-weight-bold text-red-500">
          {{ $t('shop.reserveQueueBlockedTitle') }}
        </div>
        {{ $t('shop.reserveQueueBlockedMessage', { count: blockedItems.length }) }}
      </div>

      <hr>

      <button
        class="btn btn-sm btn-primary btn-block"
        @click="showBlockedItems = false"
      >
        {{ $t('general.close') }}
      </button>
    </BModal>

    <div class="orders-list-body">
      <OrdersFlatList
        v-if="groupBy === 'none'"
        :payers="payers"
        :dates="dates"
        :orders="orders['none'] || []"
        :edited-id="editedId"
        :layout="layout"
        :product-sets="productSets"
        :group-by="'none'"
        :pending="listPending['none']"
        :checked="checked"
        @checked="toggle($event, true, 'none')"
        @unchecked="toggle($event, false, 'none')"
        @select="$emit('select', $event)"
      />
      <template
        v-else
      >
        <div class="header-wrapper">
          <OrderHeaderRow
            :columns="groupLayout"
            class="group-header list-padding"
          />
        </div>
        <div
          v-for="(p, index) in visibleOrders"
          :key="p.id"
        >
          <div
            class="checkbox set-row-group d-flex align-items-center"
            :class="{ 'last': index === aggregatedSubscriptions.length - 1 }"
          >
            <div
              class="select-box angle"
              @click="open(p.id)"
            >
              <i
                :class="opened[p.id] ? 'fa-solid fa-angle-down' : 'fa-solid fa-angle-right'"
              />
            </div>
            <div
              class="select-box"
              @click="checkAll(p.id)"
            >
              <Checkbox
                :value="groupChecks[p.id]"
                :pending="groupCheckPending[p.id]"
                :emit="false"
                class="px-0"
              />
            </div>
            <OrderItemRow
              :last="false"
              :columns="groupLayout"
              :order="p"
              :payer="p.payer"
              class="group-row"
              no-checkbox
            />
          </div>
          <div
            v-if="opened[p.id]"
            class="inner-table"
          >
            <OrdersFlatList
              :group-id="p.id"
              :payers="payers"
              :dates="dates"
              :orders="orders[p.id] || []"
              :pending="listPending[p.id]"
              :layout="layout"
              :product-sets="productSets"
              :edited-id="editedId"
              :group-by="groupBy"
              :checked="checked"
              @checked="toggle($event, true, p.id)"
              @unchecked="toggle($event, false, p.id)"
              @select="$emit('select', $event)"
            />
          </div>
        </div>
        <InfinityScrollCursor
          v-if="!isBottom"
          loop-check
          :margin="10"
          @request="mainCursor += 30"
        />
        <div
          v-if="pending || !isBottom"
          class="mt-4"
        >
          <Loader />
        </div>
        <div
          v-else-if="aggregatedSubscriptions.length === 0"
          class="text-center py-3 text-secondary"
        >
          <div style="font-size: 1rem">
            <span class="fa-stack fa-2x">
              <i class="far fa-box" />
              <i class="far fa-slash fa-stack-1x" />
            </span>
          </div>
          {{ $t('shop.noOrders') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import InfinityScrollCursor from '@/components/InfinityScrollCursor';
import { addSpaces } from '@/utils/filters';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import OrderHeaderRow from '../table/OrderHeaderRow';
import OrderItemRow from '../table/OrderItemRow';
import OrdersFlatList from '../table/OrdersFlatList';

export default {
  props: {
    aggregatedSubscriptions: {
      type: Array,
      default: () => [],
    },
    payers: {
      type: Array,
      default: () => [],
    },
    productSets: {
      type: Object,
      default: () => ({}),
    },
    layout: {
      type: Array,
      default: () => [],
    },
    dates: {
      type: Object,
      default: () => ({}),
    },
    filter: Object,
    editedId: String,
    groupBy: String,
    reserveQueue: Boolean,
    pending: Boolean,
  },
  data: () => ({
    pendingDownload: false,
    selectedProductSetId: null,
    mainCursor: 30,
    groupChecks: {},
    listPending: {},
    groupCheckPending: {},
    opened: {},
    cursor: {},
    checked: {},
    orders: {},
    showBulkEdit: false,
    pendingCancel: false,
    pendingMove: false,
    pendingPromote: false,
    blockedItems: [],
    showBlockedItems: false,
  }),
  components: {
    InfinityScrollCursor,
    OrderItemRow,
    OrdersFlatList,
    OrderHeaderRow,
  },
  computed: {
    ...mapGetters(['formatCurrency', 'payeeId']),
    ...mapGetters('shop', ['catalogDetails']),
    checkedOrders() {
      return Object.values(this.checked);
    },
    isBottom() {
      return this.mainCursor >= this.aggregatedSubscriptions.length;
    },
    visibleOrders() {
      return this.aggregatedSubscriptions.slice(0, this.mainCursor);
    },
    innerLayout() {
      if (this.groupBy === 'none') return this.layout;
      if (this.groupBy === 'payers') {
        return this.layout
          .filter((x) => x.key !== 'code'
            && x.key !== 'name'
            && x.type !== 'property'
            && x.type !== 'group');
      }
      if (this.groupBy === 'productSets') {
        return this.layout
          .filter((x) => x.key !== 'productSetName');
      }

      return this.layout;
    },
    groupLayout() {
      if (this.groupBy === 'none') return this.layout;
      const groupLayout = this.layout.filter((x) => !this.innerLayout.some((l) => l.key === x.key));

      return [
        ...(groupLayout.length > 0 ? [{ ...groupLayout[0], width: 400 }] : []),
        { key: 'aggregatedCount', width: 80, name: this.$t('shop.offer.aggregatedCount') },
        { key: 'aggregatedPrice', width: 130, name: this.$t('shop.offer.aggregatedPrice') },
        ...(groupLayout.length > 1 ? groupLayout.slice(1) : []),
      ];
    },
    totalPrice() {
      return this.aggregatedSubscriptions.reduce((acc, curr) => {
        acc.currency = 'pln';
        acc.price += curr.totalValue;
        return acc;
      }, { price: 0, currency: '' });
    },
    totalCount() {
      return this.aggregatedSubscriptions.reduce((acc, curr) => acc + curr.totalCount, 0);
    },
  },
  watch: {
    groupBy() {
      this.mainCursor = 30;
      this.orders = {};
      this.opened = {};

      if (this.groupBy === 'none') {
        this.request('none');
      }
    },
    filter() {
      this.mainCursor = 30;
      this.checked = {};
      this.orders = {};

      if (this.groupBy === 'none') {
        this.request('none');
      } else {
        Object.keys(this.opened)
          .forEach((key) => {
            this.request(key);
          });
      }
    },
    dates() {
      this.mainCursor = 30;
      this.checked = {};
      this.orders = {};

      if (this.groupBy === 'none') {
        this.request('none');
      } else {
        Object.keys(this.opened)
          .forEach((key) => {
            this.request(key);
          });
      }
    },
  },
  methods: {
    ...mapActions('shop', [
      'removeSubscriptions',
      'removeSubscriptionsFromReserveQueue',
      'moveSubscriptionsToReserveQueue',
      'promoteSubscriptionsFromReserveQueue',
      'getSubscriptionsList',
    ]),
    addSpaces,
    open(id) {
      this.$set(this.opened, id, !this.opened[id]);
      this.request(id);
    },
    toggle(o, value) {
      if (value) {
        this.$set(this.checked, o.id, o);
      } else {
        this.$delete(this.checked, o.id);
      }
    },
    showAddMessage() {
      const payers = Object.keys(this.checkedOrders
        .reduce((acc, curr) => { acc[curr.payerId] = true; return acc; }, {}))
        .map((x) => this.payers.find((p) => p.id === x))
        .filter(Boolean);

      this.$store.commit('setSelectedPayers', payers);
      this.$router.push(`/payee/${this.payeeId}/messages/messages?useSelectedPayers=1`);
    },
    formatDate(date) {
      return moment(date).format('HH:mm DD/MM/YYYY');
    },
    requestRemoveSubscriptions() {
      this.pendingCancel = true;
      const action = this.reserveQueue
        ? this.removeSubscriptionsFromReserveQueue
        : this.removeSubscriptions;

      action({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: this.checkedOrders.map((x) => ({
            payerId: x.payerId,
            listingId: x.listingId,
          })),
        },
      })
        .then(() => {
          this.$emit('refresh');

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]);
          });

          this.checked = {};
        })
        .finally(() => {
          this.pendingCancel = false;
        });
    },
    moveToReserveQueueChecked() {
      this.pendingMove = true;
      this.moveSubscriptionsToReserveQueue({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: this.checkedOrders.map((x) => ({
            payerId: x.payerId,
            listingId: x.listingId,
          })),
        },
      })
        .then(({ data }) => {
          this.$emit('refresh');

          const blocked = data?.blocked || [];
          const blockedKeys = blocked.reduce((acc, curr) => {
            acc[`${curr.payerId}_${curr.listingId}`] = true;
            return acc;
          }, {});

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]
              || blockedKeys[`${x.payerId}_${x.listingId}`]);
          });

          this.checked = Object.values(this.checked)
            .filter((x) => blockedKeys[`${x.payerId}_${x.listingId}`])
            .reduce((acc, curr) => { acc[curr.id] = curr; return acc; }, {});

          if (blocked.length > 0) {
            this.blockedItems = blocked;
            this.showBlockedItems = true;
          }
        })
        .finally(() => {
          this.pendingMove = false;
        });
    },
    moveToListingChecked() {
      this.$store.commit('setSelectedSubscriptions', this.checkedOrders);
      const { payeeId } = this.$route.params;
      const catalogId = this.catalogDetails.id;
      this.$router.push(
        `/payee/${payeeId}/shop/${catalogId}/subscription/move?useSelectedSubscriptions=1`,
      );
    },
    promoteCheckedFromReserveQueue() {
      this.pendingPromote = true;
      this.promoteSubscriptionsFromReserveQueue({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
          },
        },
        data: {
          items: this.checkedOrders.map((x) => ({
            payerId: x.payerId,
            listingId: x.listingId,
          })),
        },
      })
        .then(() => {
          this.$emit('refresh');

          Object.keys(this.orders).forEach((groupId) => {
            this.orders[groupId] = this.orders[groupId].filter((x) => !this.checked[x.id]);
          });

          this.checked = {};
        })
        .finally(() => {
          this.pendingPromote = false;
        });
    },
    request(groupKey, force = false) {
      if (this.orders[groupKey] && !force) {
        return Promise.resolve(this.orders[groupKey]);
      }

      this.$set(this.listPending, groupKey, true);
      return this.getSubscriptionsList({
        params: {
          query: {
            catalogId: this.catalogDetails.id,
            groupKey,
            reserveQueue: this.reserveQueue,
          },
        },
        data: {
          ...this.filter,
          groupBy: groupKey === 'none' ? 'none' : this.filter.groupBy,
        },
      })
        .then(({ data }) => {
          const parsed = data.map((s) => ({
            ...s,
            created: s.added,
            position: s.reserveQueuePosition > 0 ? s.reserveQueuePosition - 1 : 0,
            productSetId: s.listingId,
            unitPrice: s.listing?.price || 0,
            currency: s.listing?.currency || 'pln',
            listingName: s.listing?.name || '?',
          }));

          if (this.reserveQueue) {
            parsed.sort((a, b) => a.position - b.position);
          }

          this.$set(this.orders, groupKey, parsed);
          return parsed;
        })
        .finally(() => {
          this.$set(this.listPending, groupKey, false);
        });
    },
    checkAll(key) {
      if (this.groupCheckPending[key]) return;
      const value = !this.groupChecks[key];

      if (key === 'none' && !value) {
        this.checked = {};
        this.groupChecks = {};
        return;
      }

      this.$set(this.groupCheckPending, key, true);

      this.request(key)
        .then((data) => {
          this.$set(this.groupChecks, key, value);
          if (key === 'none') {
            this.aggregatedSubscriptions.forEach((o) => {
              this.$set(this.groupChecks, o.id, true);
            });
          }

          data.forEach((o) => {
            this.toggle(o, value);
          });
        })
        .finally(() => {
          this.$set(this.groupCheckPending, key, false);
        });
    },
  },
  created() {
    if (this.groupBy === 'none') {
      this.request('none');
    }
  },
};
</script>

<style lang="scss" scoped>

.select-box {
  height: 32px;
  width: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(100, 100, 100, 0.2);
  cursor: pointer;

  &.angle {
    color: #aaa;
  }

  &:hover {
    background-color: rgba(100, 100, 100, 0.1);
  }
}

.summary-label {
  font-size: 12px;
  line-height: 1.2;
  color: #888;
  text-transform: uppercase;
}

.list-padding {
  padding-left: 64px;
}

.set-row-group {
  height: 32px;
  max-height: 32px;
  border-top: 1px solid #ddd;
  width: min-content;
  min-width: 100%;

  &.last {
    border-bottom: 1px solid #ddd;
  }
}

.text-strikethrough {
  text-decoration: line-through;
}

.orders-list-body {
  height: calc(100vh - 360px);
  overflow: auto;
  padding-bottom: 20px;
}

.payer-row-content {
  padding: 5px 10px 5px 5px;
}

.column-divider {
  border-left: 1px solid rgba(100, 100, 100, 0.1);
}

.money-font {
  font-size: 0.9rem;
}

.inner-table {
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 27px;
  border: 1px solid #dddddd;
  border-radius: 10px;
  width: min-content;
}

.inner-header {
  background-color: #f6f6f6;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.group-header {
  background-color: #f6f6f6;
  width: min-content;
  min-width: 100%;
}

.collapse-icon {
  width: 27px;
  text-align: center;
  margin-left: 5px;
  line-height: 30px;
  display: flex;
  align-items: center;

  i {
    background-color: #f6f6f6;
    padding: 5px;
    border-radius: 50%;
    width: 22px;
    min-width: 22px;
    height: 22px;
    line-height: 12px;
    font-size: 15px;
    color: #666;
  }
}

.main-column {
  width: 250px;
  max-width: 250px;
  min-width: 250px;
  font-weight: 0.9rem;
}

.xs-add {
  top: 2px;
  right: 4px;
}

.header-wrapper {
  min-width: min-content;
}

.group-row {
  &:deep(.set-row) {
    border-top: none;
    cursor: default;
  }
  &:deep(.set-row-hover:hover) {
    background-color: initial;
  }
}

.orders-count {
  width: 120px;
  font-weight: 400;
  border-radius: 5px;
  text-align: right;
}

.orders-paid {
  color: $blue;
  text-align: right;
  width: 120px;
}

.orders-unpaid {
  color: $orange;
  text-align: right;
  width: 120px;
}
</style>
